import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/apiClient';
import ReserveCheckoutPanel from './ReserveCheckoutPanel';
import { createDraftClientLineId } from '../utils/draftCartLines';
import { groupPostpaidReserveItems, groupedReserveValue, expandReserveGroupValue } from '../utils/postpaidReserveGroups';

export default function PostpaidReserveDialog({ ticket, onClose, onSaved, onPrint }) {
  const [quote, setQuote] = useState(null);
  const [value, setValue] = useState({ mode: 'issue', selections: [] });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedProof, setSavedProof] = useState(null);
  const [printing, setPrinting] = useState(false);
  const printInFlight = useRef(false);
  const [requestKey] = useState(createDraftClientLineId);
  const submission = useRef(null);
  const inFlight = useRef(false);
  useEffect(() => {
    let current = true;
    const params = new URLSearchParams(ticket.paymentReference
      ? { paymentReference: ticket.paymentReference }
      : { receiptNumber: String(ticket.realCommandeId || ticket.commandeId || '').replace(/^#\s*/, '') });
    apiFetch(`/api/reserves/paid-ticket?${params}`, { includeBearer: true }).then(async response => {
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Ticket indisponible');
      if (current) {
        try {
          const pending = JSON.parse(localStorage.getItem(`moo_postpaid_reserve_pending:${payload.paymentReference}`) || 'null');
          const validPending = Array.isArray(pending?.selections) && pending.selections.length > 0 && pending.selections.every(line => Number.isSafeInteger(line.orderItemId) && line.orderItemId > 0 && Number.isSafeInteger(line.quantity) && line.quantity > 0);
          if (pending?.paymentReference === payload.paymentReference && pending.requestKey && validPending) {
            submission.current = pending;
            setValue({ mode: 'issue', customerId: pending.customerId, selections: pending.selections.map(line => ({ lineKey: String(line.orderItemId), quantity: line.quantity })) });
          } else if (pending?.paymentReference === payload.paymentReference && !validPending) {
            // Invalid/null ids were rejected before any server write. Do not
            // restore a submission that the old client could never commit.
            localStorage.removeItem(`moo_postpaid_reserve_pending:${payload.paymentReference}`);
          }
        } catch (_) {}
        setQuote(payload);
      }
    }).catch(failure => { if (current) setError(failure.message); });
    return () => { current = false; };
  }, [ticket]);
  const save = async () => {
    if (inFlight.current || !quote || savedProof) return;
    if (!submission.current && (value?.mode !== 'issue' || !value.selections?.length)) { setError('Sélectionnez les articles à mettre en réserve'); return; }
    if (!submission.current && value.selections.some(line => !Number.isSafeInteger(Number(line.lineKey)) || Number(line.lineKey) <= 0 || !quote.items.some(item => item.id === Number(line.lineKey)))) {
      setValue({ ...value, selections: [] });
      setError('Sélectionnez à nouveau les articles à mettre en réserve');
      return;
    }
    inFlight.current = true; setBusy(true); setError('');
    submission.current ||= { paymentReference: quote.paymentReference, requestKey, customerId: value.customerId || null,
      selections: value.selections.map(line => ({ orderItemId: Number(line.lineKey), quantity: line.quantity })) };
    try { localStorage.setItem(`moo_postpaid_reserve_pending:${quote.paymentReference}`, JSON.stringify(submission.current)); } catch (_) {}
    try {
      const response = await apiFetch('/api/reserves/paid-ticket', { method: 'POST', includeBearer: true,
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(submission.current) });
      const payload = await response.json();
      if (!response.ok) {
        if (response.status < 500) {
          submission.current = null;
          try { localStorage.removeItem(`moo_postpaid_reserve_pending:${quote.paymentReference}`); } catch (_) {}
        }
        throw new Error(payload.error || 'Consignation impossible');
      }
      try { localStorage.removeItem(`moo_postpaid_reserve_pending:${quote.paymentReference}`); } catch (_) {}
      setSavedProof(payload);
      await onSaved(payload);
    } catch (failure) { setError(failure.message); }
    finally { inFlight.current = false; setBusy(false); }
  };
  const print = async () => {
    if (printInFlight.current || !savedProof || typeof onPrint !== 'function') return;
    printInFlight.current = true; setPrinting(true); setError('');
    try { await onPrint(savedProof); }
    catch (failure) { setError(`${failure.message} La consignation reste enregistrée ; vous pouvez réessayer l’impression.`); }
    finally { printInFlight.current = false; setPrinting(false); }
  };
  const groups = groupPostpaidReserveItems(quote?.items || []);
  const changeGroupedValue = next => {
    try { setValue(expandReserveGroupValue(next, groups)); setError(''); }
    catch (failure) { setError(failure.message); }
  };
  return <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label="Consignation du ticket">
    <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-slate-950 p-5 text-white">
      <div className="flex justify-between"><h3 className="font-semibold">Ticket {quote?.publicReference || ticket.realCommandeId || ticket.commandeId}</h3>
        <button type="button" disabled={busy || printing} onClick={onClose} aria-label="Fermer">✕</button></div>
      {!quote && !error && <p>Chargement…</p>}
      {quote && !savedProof && <ReserveCheckoutPanel items={groups.map(group => group.item)} value={groupedReserveValue(value, groups)} onChange={changeGroupedValue}
        disabled={busy || Boolean(submission.current)} invoiceAmount={quote.invoiceMinor / 100} />}
      {!savedProof && quote && !quote.items.some(item => item.quantity > 0) && <p>Aucun article restant à consigner.</p>}
      {savedProof && <p className="my-4 text-emerald-300" role="status">Consignation enregistrée. Vous pouvez imprimer le reçu actualisé.</p>}
      {error && <p role="alert" className="my-3 text-red-300">{error}</p>}
      {savedProof ? <div className="mt-3 flex gap-3">
        {typeof onPrint === 'function' && <button type="button" onClick={print} disabled={printing || busy} className="rounded-lg bg-amber-300 px-4 py-2 font-semibold text-slate-950">{printing ? 'Impression…' : 'Imprimer'}</button>}
        <button type="button" onClick={onClose} disabled={printing || busy} className="rounded-lg border border-white/20 px-4 py-2">Terminer</button>
      </div> : <button type="button" onClick={save} disabled={busy || !quote || (!submission.current && !quote.items.some(item => item.quantity > 0))} className="mt-3 rounded-lg bg-amber-300 px-4 py-2 font-semibold text-slate-950">{busy ? 'Enregistrement…' : 'Valider'}</button>}
    </div>
  </div>;
}
